import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import authService from '../../services/auth.service';
import { FONT_MONO } from '../landing/landingData';
import { trackUserLogin } from '../../components/analytics/GoogleAnalytics';
import { AuthLead, AuthShell, AuthTitle, DangerCallout, Field, FootNote, PrimaryButton, TextLink, emailError } from './AuthShell';

const socialBtn: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, height: 44, borderRadius: 999, border: '1px solid var(--hv-line-2)', background: 'transparent', color: 'var(--hv-ink)', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 600, cursor: 'pointer' };

export function LoginView() {
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
    const [serverError, setServerError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    // Signed-in home is the dashboard (landing stays reachable from the logo).
    const from = (location.state as { from?: Location })?.from?.pathname || '/dashboard';

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const next: typeof errors = {};
        const em = emailError(email);
        if (em) next.email = em;
        if (!password) next.password = 'Enter your password.';
        setErrors(next);
        if (Object.keys(next).length) return;

        setIsLoading(true);
        setServerError(null);
        const result = await login(email.trim(), password);
        if (result.success) {
            trackUserLogin('email');
            navigate(from, { replace: true });
            return;
        }
        setServerError(result.error || 'Login failed. Please check your credentials.');
        setIsLoading(false);
    };

    return (
        <AuthShell>
            <AuthTitle lead="Log in to" accent="HyVay" />
            <AuthLead>Pick up where you left off, your projects and media are waiting.</AuthLead>

            <form onSubmit={handleSubmit} noValidate>
                <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <Field label="Email" type="email" name="email" autoComplete="email" autoFocus value={email} onChange={(v) => { setEmail(v); setErrors((p) => ({ ...p, email: undefined })); }} placeholder="you@usaha.id" error={errors.email} />
                    <Field
                        label="Password"
                        type="password"
                        name="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(v) => { setPassword(v); setErrors((p) => ({ ...p, password: undefined })); }}
                        placeholder="••••••••"
                        error={errors.password}
                        trailing={<a href="/forgot-password" onClick={(e) => { e.preventDefault(); navigate('/forgot-password'); }} className="hv-h-underline" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--hv-brand-ink)' }}>Forgot password?</a>}
                    />
                </div>

                {serverError && <DangerCallout>{serverError}</DangerCallout>}

                <PrimaryButton disabled={isLoading}>{isLoading ? 'Logging in…' : 'Log in'}</PrimaryButton>
            </form>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '22px 0 16px' }}>
                <span style={{ flex: 1, height: 1, background: 'var(--hv-line)' }} />
                <span style={{ fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: '0.06em', color: 'var(--hv-muted)' }}>OR CONTINUE WITH</span>
                <span style={{ flex: 1, height: 1, background: 'var(--hv-line)' }} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <button type="button" disabled={isLoading} data-track="login_github" onClick={() => { window.location.href = authService.getGithubLoginUrl(); }} className="hv-h-mint-bd" style={socialBtn}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.2A9.8 9.8 0 0 0 8.9 21.3c.5.1.7-.2.7-.5v-1.9c-2.7.6-3.3-1.3-3.3-1.3-.4-1.1-1.1-1.4-1.1-1.4-.9-.6.1-.6.1-.6 1 .1 1.5 1 1.5 1 .9 1.5 2.3 1.1 2.9.8.1-.7.4-1.1.6-1.4-2.2-.3-4.5-1.1-4.5-4.9 0-1.1.4-2 1-2.7-.1-.3-.4-1.3.1-2.7 0 0 .8-.3 2.7 1a9.3 9.3 0 0 1 5 0c1.9-1.3 2.7-1 2.7-1 .5 1.4.2 2.4.1 2.7.6.7 1 1.6 1 2.7 0 3.8-2.3 4.6-4.5 4.9.4.3.7.9.7 1.9v2.8c0 .3.2.6.7.5A9.8 9.8 0 0 0 12 2.2z" /></svg>
                    GitHub
                </button>
                <button type="button" disabled={isLoading} data-track="login_google" onClick={() => { window.location.href = authService.getGoogleLoginUrl(); }} className="hv-h-mint-bd" style={socialBtn}>
                    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><path d="M23 12.2c0-.8-.07-1.56-.2-2.29H12v4.34h6.16a5.27 5.27 0 0 1-2.29 3.46v2.88h3.7C21.72 18.6 23 15.7 23 12.2z" fill="#4285F4" /><path d="M12 23.5c2.97 0 5.46-.98 7.28-2.66l-3.55-2.76c-.98.66-2.24 1.05-3.73 1.05-2.87 0-5.3-1.94-6.16-4.55H2.18v2.87A11.5 11.5 0 0 0 12 23.5z" fill="#34A853" /><path d="M5.84 14.58a6.9 6.9 0 0 1 0-4.4V7.31H2.18a11.5 11.5 0 0 0 0 10.14l3.66-2.87z" fill="#FBBC05" /><path d="M12 5.23c1.62 0 3.06.56 4.2 1.64l3.15-3.15C17.45 1.97 14.96 1 12 1 7.7 1 3.99 3.47 2.18 7.31l3.66 2.87C6.7 7.57 9.13 5.23 12 5.23z" fill="#EA4335" /></svg>
                    Google
                </button>
            </div>

            <FootNote>
                Don't have an account? <TextLink to="/signup" onClick={() => navigate('/signup')}>Sign up</TextLink>
            </FootNote>
        </AuthShell>
    );
}
